import { CommonModule } from '@angular/common';
import { Component, ContentChild, OnInit, TemplateRef } from '@angular/core';
import { HandleLoadingState } from '../../../../../decorators/handle-loading-state.decorator';
import { downloadFile } from '../../../../../functions/download-file';
import { getClaimKey } from '../../../../../functions/get-claim-key';
import { ClaimRow } from '../../../../../models/api/view/claim-view/claim-row';
import { Attributes } from '../../../../../models/api/view/claim/details/attributes/attributes';
import { EventData } from '../../../../../models/api/view/claim/details/casualty/event-data';
import { Subowner } from '../../../../../models/api/view/claim/details/disability/subowner';
import { BaseComponent } from '../../../../base/base.component';
import { LinkLabelComponent } from '../../../../core/labels/link-label.component';
import { CardComponent } from '../../../../core/panels/card.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../../core/panels/loading-panel.component';
import { TogglePanelComponent } from '../../../../core/panels/toggle-panel.component';
import { ColumnComponent } from '../../../../core/table/column.component';
import { TableComponent } from '../../../../core/table/table.component';
import { PolicyAttributesComponent } from '../../../policy-details/policy-attributes.component';
import { RelatedClaimsComponent } from '../../../related-claims/related-claims.component';
import { StatusHistoryComponent } from '../../../status-history/status-history.component';

@Component({
  selector: 'app-casualty',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    TogglePanelComponent,
    RelatedClaimsComponent,
    StatusHistoryComponent,
    ColumnComponent,
    TableComponent,
    PolicyAttributesComponent,
    CommonModule,
    LinkLabelComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'" [error]="error">
      <ng-template #loaded>
        <div class="tw-flex tw-w-full tw-gap-8 tw-pt-8">
          <div class="tw-w-full">
            <app-card label="Loss">
              <div class="table-scroll">
                <div class="tw-flex tw-w-full tw-gap-8">
                  <app-detail-panel
                    [dataSource]="eventData?.loss"
                    class="tw-basis-1/3"
                  >
                    <app-detail-item label="event number" name="eventNumber" />
                    @if (detailAuth && showCreated) {
                      <app-detail-item
                        label="created"
                        name="created"
                        format="d"
                      />
                    }
                    @if (detailAuth && showOpened) {
                      <app-detail-item
                        label="opened"
                        name="opened"
                        format="d"
                      />
                    }
                    @if (detailAuth && showClosed) {
                      <app-detail-item
                        label="closed"
                        name="closed"
                        format="d"
                      />
                    }
                    @if (showLossDescription) {
                      <app-detail-item
                        label="loss description"
                        name="lossDescription"
                      />
                    }
                  </app-detail-panel>
                  <app-detail-panel
                    [dataSource]="eventData?.loss"
                    class="tw-basis-1/3"
                  >
                    @if (detailAuth && showReportedToClient) {
                      <app-detail-item
                        label="Reported to client"
                        name="reportedToClient"
                        format="d"
                      />
                    }
                    @if (detailAuth && showReportedByClient) {
                      <app-detail-item
                        label="Reported by client"
                        name="reportedByClient"
                        format="dt"
                      />
                    }
                    @if (detailAuth && showReportedToTPA) {
                      <app-detail-item
                        label="Reported to TPA"
                        name="reportedToTPA"
                        format="d"
                        labelCase="none"
                      />
                    }
                    <app-detail-item label="jurisdiction" name="jurisdiction" />
                    @if (detailAuth) {
                      <app-detail-item
                        label="excess status"
                        name="excessStatus"
                      />
                    }
                  </app-detail-panel>
                  <app-detail-panel
                    [dataSource]="eventData?.loss"
                    class="tw-basis-1/3"
                  >
                    @if (detailAuth) {
                      <app-detail-item
                        label="MPN eligible"
                        name="mpnEligible"
                        labelCase="none"
                        format="b"
                      />
                    }
                    @if (detailAuth) {
                      <app-detail-item
                        label="Eligibility date"
                        name="eligibilityDate"
                        format="d"
                      />
                    }
                    @if (detailAuth) {
                      <app-detail-item label="network" name="network" />
                    }
                    @if (detailAuth) {
                      <app-detail-item label="claim type" name="claimType" />
                    }
                    @if (detailAuth && showSubType) {
                      <app-detail-item label="subtype" name="subtype" />
                    }
                  </app-detail-panel>
                </div>
              </div>
            </app-card>
          </div>
        </div>

        <div class="table-scroll">
          <ng-container *ngTemplateOutlet="detailTemplate"></ng-container>
        </div>

        <div class="tw-pt-8"></div>
        <app-toggle-panel #togglePanel [expanded]="breakpoint.isMobile()">
          @if (showCorporateStructure()) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="corporate structure"
                class="tw-grow tw-min-w-0"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
              >
                <div class="table-scroll">
                  <app-table
                    [dataSource]="eventData?.corporateStructure || []"
                    [pageSize]="10"
                    [scrollable]="true"
                    [showSort]="false"
                    [showToolbar]="false"
                    recordLabel="record"
                  >
                    <app-column name="level" label="Level" />
                    <app-column
                      name="contractAccountUnit"
                      label="Contract/account/unit"
                    />
                    <app-column name="structureCode" label="Structure code" />
                    <app-column name="name" label="Name" />
                  </app-table>
                </div>
              </app-card>
            </div>
          }
          @if (detailAuth) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="Related claims"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
                class="tw-grow card-table-scroll-container"
              >
                <div class="card-table-scroll">
                  <app-related-claims
                    [pageSize]="10"
                    sortField="claimantFirstName"
                    [sortOrder]="1"
                  />
                </div>
              </app-card>
            </div>
          }
          @if (showStatusHistoryCard) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="Status History"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
                class="tw-grow card-table-scroll-container"
              >
                <div class="card-table-scroll">
                  <app-status-history />
                </div>
              </app-card>
            </div>
          }
          @if (eventData?.catastrophes && eventData.catastrophes.length) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="catastrophe"
                class="tw-grow tw-min-w-0"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
              >
                <div class="table-scroll">
                  <app-table
                    [dataSource]="eventData.catastrophes"
                    [pageSize]="10"
                    [scrollable]="true"
                    [showToolbar]="false"
                    recordLabel="catastrophe"
                  >
                    <app-column
                      name="catastropheNumber"
                      label="catastrophe number"
                      [sort]="false"
                    >
                      <ng-template let-data>
                        <app-link-label
                          (click)="
                            downloadCatastrophe(
                              data.catastropheUid,
                              data.fileName,
                              $event
                            )
                          "
                        >
                          {{ data.catastropheNumber }}
                        </app-link-label>
                      </ng-template>
                    </app-column>
                    <app-column
                      name="catastropheName"
                      label="catastrophe name"
                      [sort]="false"
                    />
                  </app-table>
                </div>
              </app-card>
            </div>
          }
          @if (this.subOwnerAuth && subowner.length) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="sub-owners"
                class="tw-grow tw-min-w-0"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
              >
                <div class="table-scroll">
                  <app-table
                    [dataSource]="subowner"
                    [pageSize]="10"
                    [scrollable]="true"
                    [showFilter]="false"
                    recordLabel="sub-owner"
                    [globalFilterFields]="[
                      'subownerType',
                      'fullName',
                      'dateCreated',
                      'dateExpire',
                      'phone',
                      'email',
                    ]"
                  >
                    <app-column name="subownerType" label="Type" />
                    <app-column name="lastName" label="Name">
                      <ng-template let-data>
                        {{ data.firstName }} {{ data.lastName }}
                      </ng-template>
                    </app-column>
                    <app-column name="dateCreated" label="Begin" format="d" />
                    <app-column name="dateExpire" label="End" format="d" />
                    <app-column name="phone" label="Phone" format="p" />
                    <app-column name="email" label="Email" />
                  </app-table>
                </div>
              </app-card>
            </div>
          }
          @if (this.claimAttributesAuth && attributes.length) {
            <div class="tw-flex tw-gap-4 tw-pb-4 tw-min-w-0">
              <app-card
                label="Attributes"
                [expanded]="togglePanel.expandAll.value"
                [showExpand]="true"
                class="tw-grow card-table-scroll-container"
              >
                <div class="card-table-scroll">
                  <app-policy-attributes
                    [dataSource]="attributes"
                    [pageSize]="10"
                    [source]="claim.lineOfBusiness"
                  />
                </div>
              </app-card>
            </div>
          }
        </app-toggle-panel>
      </ng-template>
    </app-loading-panel>
  `,
  styles: `
    .table-scroll {
      width: 100%;
      min-width: 0;
      overflow-x: auto;
    }

    .table-scroll ::ng-deep table {
      min-width: max-content;
      width: 100%;
      border-collapse: collapse;
    }

    /* Card tables (Status History, Attributes): horizontal scroll shows full content */
    .card-table-scroll-container {
      min-width: 0;
    }

    .card-table-scroll {
      width: 100%;
      min-width: 0;
      overflow-x: auto;
      overflow-y: visible;
      -webkit-overflow-scrolling: touch;
    }

    .card-table-scroll ::ng-deep table {
      min-width: max-content;
      width: 100%;
      border-collapse: collapse;
    }
  `,
})
export class CasualtyComponent extends BaseComponent implements OnInit {
  @ContentChild('detail')
  detailTemplate: TemplateRef<any>;
  claim: ClaimRow;
  eventData?: EventData;
  subowner: Subowner[] = [];
  attributes: Attributes[] = [];

  // Card visibility controlled by security tokens
  subOwnerAuth = false;
  claimAttributesAuth = false;
  detailAuth = false;
  showStatusHistoryCard = false;

  // Loss description visibility (NME/query groups — not gated by OPTIONS-DETAIL)
  showLossDescription = false;

  // 3.6.014: WEB RESTRICT hides these even when Details is allowed
  showCreated = true;
  showOpened = true;
  showClosed = true;
  showReportedToClient = true;
  showReportedByClient = true;
  showReportedToTPA = true;
  showSubType = true;

  loading = false;
  error: any = null;

  constructor() {
    super();
  }

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.subOwnerAuth = this.session.authorize('Subowner');
    this.claimAttributesAuth = this.session.authorize('ClaimAttributes');
    this.detailAuth = this.session.authorize('Details');
    this.showStatusHistoryCard = this.session.authorize('ClaimStatus');
    this.initializeFieldSecurity();

    this.claim = this.view.currentClaim();

    this.eventData = await this.api.casualty.getEventData({
      recordSource: this.claim.recordSource,
      recordId: this.claim.recordId,
    });

    if (this.subOwnerAuth) {
      const data = await this.api.casualty.subowner(getClaimKey());
      this.subowner = data.map((item) => ({
        ...item,
        fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
      }));
    }

    if (this.claimAttributesAuth) {
      this.attributes = await this.api.casualty.attributes(
        getClaimKey(),
        Number(this.claim.contractNumber),
      );
    }
  }

  private initializeFieldSecurity(): void {
    const restrictedFields =
      this.session.user?.fieldSecurity?.restrictedFields || [];
    this.showLossDescription = !restrictedFields.includes('LossDescription');
    this.showCreated = !restrictedFields.includes('Created');
    this.showOpened = !restrictedFields.includes('Opened');
    this.showClosed = !restrictedFields.includes('Closed');
    this.showReportedToClient = !restrictedFields.includes('ReportedToClient');
    this.showReportedByClient = !restrictedFields.includes('ReportedByClient');
    this.showReportedToTPA = !restrictedFields.includes('ReportedToTPA');
    this.showSubType = !restrictedFields.includes('SubType');
  }

  /**
   * Check if Corporate Structure card should be shown
   */
  showCorporateStructure(): boolean {
    return !this.session.user?.fieldSecurity?.suppressCorporateStructure;
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async downloadCatastrophe(
    catastropheUid: number,
    fileName: string,
    event?: Event,
  ): Promise<void> {
    if (event) {
      event.stopImmediatePropagation();
    }
    const fileData =
      await this.api.casualty.downloadCatastrophe(catastropheUid);
    downloadFile(fileData, fileName, fileData.type);
  }
}
